import { ExternalLink } from '@uikit';

export const keyFeatures = [
  {
    id: 'multi-chain-compatibility',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Multi-Chain Compatibility:&nbsp;</span>
        LiveArt integrates with major blockchain networks, enabling global accessibility and the
        flexibility for users to interact with their assets across different chains.
      </span>
    ),
  },
  {
    id: 'ai-powered-analytics',
    item: (
      <span className="font-normal">
        <span className="font-semibold">AI-Powered Analytics:&nbsp;</span>
        LiveArt leverages a proprietary database of over 10 million data points to provide real-time
        insights into artwork valuations, market trends, and predictive data. This allows investors
        to make informed decisions with transparency and depth that traditional markets lack.
      </span>
    ),
  },
  {
    id: 'fractional-ownership',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Fractional Ownership:&nbsp;</span>
        The platform enables fractional ownership of blue-chip artworks, allowing users to invest in
        high-value pieces at a fraction of the cost, democratizing access to iconic assets typically
        reserved for elite collectors.
      </span>
    ),
  },
  {
    id: 'gamified-experience',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Gamified Experience:&nbsp;</span>
        Through the $ART Points Universe, LiveArt encourages user engagement by offering quests,
        interactive elements, and rewards that foster community involvement. Users earn points by
        participating in the ecosystem, incentivizing a deeper connection to the art they invest in.
      </span>
    ),
  },
  {
    id: 'player-owned',
    item: (
      <span className="font-normal">
        <span className="font-semibold">DeFi Integration:&nbsp;</span>
        LiveArt combines art with decentralized finance by offering products such as art indices and
        on-chain loans. These options bring liquidity and traditional financial mechanisms to the
        art market, allowing users to access advanced financial tools to support their art
        investments.
      </span>
    ),
  },
];

export const howToParticipate = [
  {
    id: 'create-a-magic-launchpad-account',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Create a Magic Launchpad Account: &nbsp;</span>
        Sign up for an account on Magic Launchpad.
      </span>
    ),
  },
  {
    id: 'complete-kyc',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Complete KYC:&nbsp;</span>
        Verify your identity by&nbsp;
        <ExternalLink
          href="https://docs.magicsquare.io/magic-launchpad/getting-started"
          className="text-base-text-blue underline"
        >
          completing KYC
        </ExternalLink>
        &nbsp;through SumSub or by verifying your Binance BABT.
      </span>
    ),
  },
];

export const tractions = [
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">130K&nbsp;</span>
        Twitter followers
      </span>
    ),
  },
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">90K&nbsp;</span>
        Discord members
      </span>
    ),
  },
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">15K&nbsp;</span>
        Telegram members
      </span>
    ),
  },
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">10K&nbsp;</span>
        YouTube subscribers
      </span>
    ),
  },
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">$3.5 billion&nbsp;</span>
        in trading volume in the past 4 months
      </span>
    ),
  },
  {
    id: 'twitter-traction',
    item: (
      <span className="font-normal">
        <span className="font-semibold">$150,000&nbsp;</span>
        in revenue pre-token launch
      </span>
    ),
  },
];

export const businessModel = [
  {
    id: 'trading-fees',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Trading Fees:&nbsp;</span>
        The platform generates fees from transactions involving high-value artworks on its Global
        Trading Floor, where collectors can buy and sell iconic pieces.
      </span>
    ),
  },
  {
    id: 'nft-sales',
    item: (
      <span className="font-normal">
        <span className="font-semibold">NFT Sales and Royalties:&nbsp;</span>
        Through its Digital Art Studio, LiveArt offers limited-edition digital collections, earning
        from primary sales and ongoing royalties on resales.
      </span>
    ),
  },
  {
    id: 'data-licensing',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Data Licensing:&nbsp;</span>
        The platform licenses its proprietary AI-powered art data, offering insights to
        institutions, collectors, and other stakeholders interested in real-time market trends and
        valuation data.
      </span>
    ),
  },
  {
    id: 'fractional-ownership',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Fractional Ownership Fees:&nbsp;</span>
        LiveArt offers fractionalized investments, allowing users to own parts of iconic art assets,
        and generates revenue from ownership fees.
      </span>
    ),
  },
  {
    id: 'defi-products',
    item: (
      <span className="font-normal">
        <span className="font-semibold">DeFi Products:&nbsp;</span>
        Art indices, on-chain loans, and liquidity options make art assets more dynamic and
        accessible, introducing traditional finance mechanisms like indices and loans into the art
        investment space.
      </span>
    ),
  },
];

export const partners = [
  {
    name: 'Binance Labs',
    image: '/launchpad/stories/liveart/investors/binance-labs.png',
    width: 254,
    height: 72,
    className: 'py-[5px] px-[5px]',
  },
  {
    name: 'Animica Brands',
    image: '/launchpad/stories/liveart/investors/animica-brands.png',
    width: 254,
    height: 80,
    className: 'py-[8px] px-[8px]',
  },
  {
    name: 'Hashkey Capital',
    image: '/launchpad/stories/liveart/investors/haskey-capital.png',
    width: 254,
    height: 72,
    className: 'py-[8px] px-[8px]',
  },
  {
    name: 'Samsung Venture Investment',
    image: '/launchpad/stories/liveart/investors/samsung-venture-investment.png',
    width: 274,
    height: 38,
    className: 'py-[17px] px-[17px]',
  },
  {
    name: 'OKX Ventures',
    image: '/launchpad/stories/liveart/investors/okx-ventures.png',
    width: 384,
    height: 72,
    className: 'py-[8px] px-[8px]',
  },
];

export const tokenUtility = [
  {
    id: 'exclusive-access',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Exclusive Access:&nbsp;</span>
        Grants token holders access to unique art collections, limited edition NFTs, and
        high-profile art drops.
      </span>
    ),
  },
  {
    id: 'fractional-ownership',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Fractional Ownership:&nbsp;</span>
        Token holders can participate in fractionalized ownership of iconic assets, democratizing
        access to high-value art.
      </span>
    ),
  },
  {
    id: 'rewards-and-staking',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Rewards and Staking:&nbsp;</span>
        The platform encourages engagement through a staking and rewards system, where users earn
        for active participation in the ecosystem.
      </span>
    ),
  },
  {
    id: 'defi-products',
    item: (
      <span className="font-normal">
        <span className="font-semibold">DeFi Products:&nbsp;</span>
        $ART token holders can access art indices and loans, participate in governance, and utilize
        various financial products that enhance the art investment experience.
      </span>
    ),
  },
];

export const roadmap = [
  {
    id: 'q4',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Q4 2024:&nbsp;</span>
        Launch of $ART token and introduction of gamified Real-World Asset (RWA) purchasing.
      </span>
    ),
  },
  {
    id: 'q1',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Q1 2025:&nbsp;</span>
        Launch of a secondary marketplace for fractional art ownership and rollout of onboarding
        campaigns to grow the user base.
      </span>
    ),
  },
  {
    id: 'q2',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Q2 2025:&nbsp;</span>
        Expanded multi-chain compatibility to reach a wider Web3 audience.
      </span>
    ),
  },
  {
    id: 'q3',
    item: (
      <span className="font-normal">
        <span className="font-semibold">Q3 2025:&nbsp;</span>
        Onboarding of Web2 users to bring new collectors to the blockchain, enabling broader
        participation in fractionalized art ownership.
      </span>
    ),
  },
];

export const marketTraction = [
  {
    id: 'unique-wallets',
    item: (
      <span className="font-normal">
        <span className="font-semibold">12M+ unique active wallets,&nbsp;</span>
        with over <span className="font-semibold">1M+</span> weekly active wallets.
      </span>
    ),
  },
  {
    id: 'interactions',
    item: (
      <span className="font-normal">
        <span className="font-semibold">20,000-25,000 on-chain DAUs&nbsp;</span>
        through smart contract interactions.
      </span>
    ),
  },
  {
    id: 'unique-wallets',
    item: (
      <span className="font-normal">
        <span className="font-semibold">1.4M+ social media following,&nbsp;</span>
        including an active
        <span className="font-semibold">&nbsp;450,000+ Twitter/X community.</span>
      </span>
    ),
  },
  {
    id: 'global-reach',
    item: (
      <span className="font-normal">
        <span className="font-semibold">A 30M+ global reach&nbsp;</span>
        spanning high-liquidity chains, top wallets, social quest platforms, marketplaces, and
        crypto KOLs.
      </span>
    ),
  },
  {
    id: 'ranked',
    item: (
      <span className="font-normal">
        Ranked as a<span className="font-semibold">&nbsp;Top-15 Telegram mini-app&nbsp;</span>by
        on-chain user engagement on TON.
      </span>
    ),
  },
  {
    id: 'top-projects',
    item: (
      <span className="font-normal">
        Among the<span className="font-semibold">&nbsp;Top-5 Polygon projects&nbsp;</span>by
        on-chain transactions.
      </span>
    ),
  },
  {
    id: 'number-one',
    item: (
      <span className="font-normal">
        Recognized as the<span className="font-semibold">&nbsp;#1 provider&nbsp;</span>of global art
        market data and analytics.
      </span>
    ),
  },
];
